/**
 * Finals — the rotation, and who may see what while it runs.
 *
 * A finals session is not just a shorter round: the finalists climb the same
 * boulders in a rotation, one competitor per boulder at a time, and a climber
 * waiting in isolation must not learn how the others did. So the two things
 * here are the rotation matrix (who is on which boulder in which round) and
 * `getClimberVisibility`, which is the isolation rule expressed as data.
 *
 * `getFinalsGroupsInfo` turns sessions into the virtual "G — FINALS" groups
 * every board renders alongside the real ones.
 */

/**
 * The `finals` field of a NEW finals session — what the editor's "add finals
 * session" starts from (no finalists, no routes, the IFSC default timings, no
 * phase yet). The E2E seed starts its finals session from the same object.
 * `totalRounds` is informational and follows `getFinalsTimeline` once finalists
 * and routes are set.
 */
export function newFinalsConfig() {
  return {
    topN: 8,
    finalistUids: [],
    climbingOrder: [],
    routeIds: [],
    timePerBoulder: 240,
    restBetweenRounds: 60,
    restRounds: 1,
    observationTime: 120,
    currentRound: null,
    totalRounds: 0,
    phase: null,
  };
}

/**
 * Number of rounds between a finalist's consecutive boulders, derived from the
 * configured `restRounds`. `step = restRounds + 1`; clamped so every climber
 * always rests at least one round (the rotation collapses otherwise).
 */
export function finalsRotationStep(restRounds) {
  const n = Math.floor(Number(restRounds));
  return (Number.isFinite(n) ? Math.max(1, n) : 1) + 1;
}

/**
 * Compute the rotation matrix for a finals session.
 * Returns an array of rounds, each containing assignments: { uid, routeId, boulderIndex, climberIndex }.
 * Deterministic — no Firestore storage needed.
 *
 * IFSC boulder-finals cadence: every climber gets `restRounds` rest/transition
 * rounds between boulders (climb → rest… → climb → rest… …). With the default
 * restRounds = 1 a climber's boulder b is at round = climberIndex + 2*b (their
 * odd offsets are rest rounds) — the classic IFSC cadence. Some events give two
 * rotations of rest, so the spacing between a climber's consecutive boulders is
 * `step = restRounds + 1` rounds and boulder b sits at round = climberIndex +
 * step*b. On a given boulder, successive climbers still enter on consecutive
 * rounds, so the wall stays busy while each individual alternates climb/rest.
 * Total rounds = numClimbers + step*(numBoulders − 1).
 *
 * `restRounds` is clamped to a minimum of 1 (every climber always gets at least
 * one rest round between boulders — anything less collapses the rotation).
 */
export function getRotationMatrix(climbingOrder, routeIds, restRounds = 1) {
  const numClimbers = climbingOrder.length;
  const numBoulders = routeIds.length;
  if (numClimbers === 0 || numBoulders === 0) return [];
  const step = finalsRotationStep(restRounds);
  const totalRounds = numClimbers + step * (numBoulders - 1);

  const rounds = [];
  for (let round = 0; round < totalRounds; round++) {
    const assignments = [];
    for (let b = 0; b < numBoulders; b++) {
      const climberIndex = round - step * b;
      if (climberIndex >= 0 && climberIndex < numClimbers) {
        assignments.push({
          uid: climbingOrder[climberIndex],
          routeId: routeIds[b],
          boulderIndex: b,
          climberIndex,
        });
      }
    }
    rounds.push(assignments);
  }
  return rounds;
}

/**
 * Normalize a finals session into an ORDERED list of per-group configs:
 *   [{ groupId, climbingOrder:[uid…], routeIds:[rid…], maxAttempts }]
 *
 * A multi-group finals does NOT mix climbers — each group keeps its own
 * ordered finalist list and its own routes; groups run on one continuous
 * timeline (see getFinalsTimeline), separated by `finals.groupGapRounds`.
 *
 * Backward compatible: a legacy finals session (single `finals.climbingOrder`
 * + `finals.routeIds`, no `groupConfigs`) collapses to ONE config so every
 * existing consumer behaves exactly as before.
 */
export function getFinalsGroupConfigs(session) {
  const f = session && session.finals;
  if (!f) return [];
  if (Array.isArray(f.groupConfigs) && f.groupConfigs.length) {
    return f.groupConfigs
      .map(c => ({
        groupId: c && c.groupId != null ? c.groupId : null,
        climbingOrder: Array.isArray(c && c.climbingOrder) ? c.climbingOrder.filter(Boolean) : [],
        routeIds: Array.isArray(c && c.routeIds) ? c.routeIds.filter(Boolean) : [],
        maxAttempts: (c && c.maxAttempts != null) ? c.maxAttempts : (f.maxAttempts != null ? f.maxAttempts : null),
      }))
      .filter(c => c.climbingOrder.length || c.routeIds.length);
  }
  // Legacy single merged config
  const climbingOrder = ((f.climbingOrder && f.climbingOrder.length) ? f.climbingOrder : f.finalistUids) || [];
  const routeIds = f.routeIds || [];
  if (!climbingOrder.length && !routeIds.length) return [];
  return [{
    groupId: (session.groupIds && session.groupIds[0]) || null,
    climbingOrder: climbingOrder.filter(Boolean),
    routeIds: routeIds.filter(Boolean),
    maxAttempts: f.maxAttempts != null ? f.maxAttempts : null,
  }];
}

/**
 * Build the combined finals timeline across all per-group configs.
 *
 * Each group rotates on its OWN routes via getRotationMatrix. Group k's
 * global round offset = offset(k-1) + climbers(k-1) + gap, so the next
 * group's first climber enters `gap` rounds after the previous group's
 * last climber ENTERED — the previous group may still be finishing its
 * own diagonal while the next group's routes sit idle during the gap.
 *
 * Returns:
 *   { configs, groups:[{…cfg, offset, colBase, localRounds}], columns:
 *     [{groupId, routeId, groupBoulderIndex, colIndex}], totalCols,
 *     totalRounds, gap,
 *     rounds: round[] where round = [{uid, routeId, groupId,
 *       boulderIndex(global col), groupBoulderIndex, climberIndex}] }
 */
export function getFinalsTimeline(session) {
  const configs = getFinalsGroupConfigs(session);
  const gap = Math.max(0, Number(session && session.finals && session.finals.groupGapRounds) || 0);
  const restRounds = session && session.finals ? session.finals.restRounds : 1;

  let offset = 0;
  let colBase = 0;
  const groups = [];
  for (const cfg of configs) {
    const local = getRotationMatrix(cfg.climbingOrder, cfg.routeIds, restRounds);
    groups.push({ ...cfg, offset, colBase, local, localRounds: local.length });
    offset += cfg.climbingOrder.length + gap;
    colBase += cfg.routeIds.length;
  }

  const totalRounds = groups.reduce((m, g) => Math.max(m, g.offset + g.localRounds), 0);
  const columns = [];
  for (const g of groups) {
    g.routeIds.forEach((rid, lb) => columns.push({
      groupId: g.groupId, routeId: rid, groupBoulderIndex: lb, colIndex: g.colBase + lb,
    }));
  }

  const rounds = [];
  for (let r = 0; r < totalRounds; r++) {
    const assignments = [];
    for (const g of groups) {
      const lr = r - g.offset;
      if (lr >= 0 && lr < g.localRounds) {
        for (const a of g.local[lr]) {
          assignments.push({
            uid: a.uid,
            routeId: a.routeId,
            groupId: g.groupId,
            boulderIndex: g.colBase + a.boulderIndex,
            groupBoulderIndex: a.boulderIndex,
            climberIndex: a.climberIndex,
          });
        }
      }
    }
    rounds.push(assignments);
  }

  return { configs, groups, columns, totalCols: colBase, totalRounds, gap, rounds };
}

/** All finalist uids across every group config (de-duplicated, order preserved). */
export function getFinalsAllFinalists(session) {
  const out = [];
  const seen = new Set();
  for (const c of getFinalsGroupConfigs(session)) {
    for (const uid of c.climbingOrder) { if (!seen.has(uid)) { seen.add(uid); out.push(uid); } }
  }
  return out;
}

/** Combined-timeline assignments for a 1-indexed round (or [] ). */
export function getFinalsRoundAssignments(session, round) {
  const tl = getFinalsTimeline(session);
  return tl.rounds[(round || 1) - 1] || [];
}

/**
 * Determine what a finalist can see based on current round and isolation rules.
 * Returns { isolated, visibleRouteIds, entryRound, currentBoulder }.
 * Non-finalists (spectators, judges) see everything. Group-aware: a climber's
 * entry round is their position WITHIN their group's order plus that group's
 * global timeline offset; they only ever see their own group's routes.
 */
export function getClimberVisibility(session, currentUid, currentRound) {
  if (!session || session.type !== "finals" || !session.finals) {
    return { isolated: false, visibleRouteIds: null };
  }
  const tl = getFinalsTimeline(session);
  const allRouteIds = tl.columns.map(c => c.routeId);
  const phase = session.finals.phase;

  // During observation or finished phase, everyone sees all routes
  if (phase === "observation" || phase === "finished") {
    return { isolated: false, visibleRouteIds: allRouteIds };
  }

  const grp = tl.groups.find(g => g.climbingOrder.includes(currentUid));
  // Not a finalist — spectator/judge, sees everything
  if (!grp) {
    return { isolated: false, visibleRouteIds: allRouteIds };
  }

  const climberIndex = grp.climbingOrder.indexOf(currentUid);
  const entryRound = grp.offset + climberIndex; // 0-indexed global entry
  const currentRoundIndex = (currentRound || 1) - 1;

  if (currentRoundIndex < entryRound) {
    // Not yet entered — full isolation
    return { isolated: true, visibleRouteIds: [], entryRound: entryRound + 1 };
  }

  // Climb/rest cadence: boulder b is at offset step*b from entry (step =
  // restRounds + 1); the in-between offsets are rest rounds. Boulders seen so
  // far (incl. the one being climbed / just climbed during the following rest)
  // and the current boulder.
  const step = finalsRotationStep(session.finals.restRounds);
  const delta = currentRoundIndex - entryRound;
  const boulderPos = Math.floor(delta / step);
  const isResting = delta % step !== 0;
  const bouldersClimbed = Math.min(grp.routeIds.length, boulderPos + 1);
  const visibleRouteIds = grp.routeIds.slice(0, bouldersClimbed);
  const currentBoulder = (!isResting && boulderPos < grp.routeIds.length) ? grp.routeIds[boulderPos] : null;

  return { isolated: false, visibleRouteIds, entryRound: entryRound + 1, currentBoulder };
}

/**
 * Get finals info for groups that have finals sessions.
 * Returns array of { groupId, groupName, finalsSessionIds, finalistUids, finalsRouteIds }.
 */
export function getFinalsGroupsInfo(groups, sessions) {
  const finalsSessions = (sessions || []).filter(s => s.type === "finals" && s.finals);
  if (finalsSessions.length === 0) return [];

  const result = [];
  for (const g of (groups || [])) {
    const groupFinals = finalsSessions.filter(s => (s.groupIds || []).includes(g.id));
    if (groupFinals.length === 0) continue;
    // Per-group config aware: pick this group's own config when present;
    // legacy single-config sessions fall back to the merged list (so every
    // group in the session keeps the historical behaviour).
    const finalsSessionIds = groupFinals.map(s => s.id);
    const uidSet = [];
    const routeSet = [];
    let finalsMaxAttempts = null;
    for (const s of groupFinals) {
      const cfgs = getFinalsGroupConfigs(s);
      let cfg = cfgs.find(c => c.groupId === g.id);
      if (!cfg && cfgs.length === 1 && cfgs[0].groupId == null) cfg = cfgs[0];
      if (!cfg) continue;
      uidSet.push(...cfg.climbingOrder);
      routeSet.push(...cfg.routeIds);
      const v = cfg.maxAttempts;
      if (v != null && v > 0 && v > (finalsMaxAttempts || 0)) finalsMaxAttempts = v;
    }
    const finalistUids = [...new Set(uidSet)];
    const finalsRouteIds = [...new Set(routeSet)];
    // Use the first finals session's name as the display name
    const finalsSessionName = groupFinals[0]?.name || null;
    result.push({
      groupId: g.id,
      groupName: g.name,
      groupColor: g.color,
      groupOrder: g.order,
      finalsSessionIds,
      finalistUids,
      finalsRouteIds,
      finalsSessionName,
      finalsMaxAttempts,
    });
  }
  return result;
}
